import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useSelector } from "react-redux";
import { flatten, get } from "lodash";
import { groupByWagerType } from "@tvg/design-system";
import { getBetSelections } from "../../../../store/selectors";
import { Container } from "./styled-components";
import {
  BetTypeListItem,
  BetTypeSelectorProps,
  GroupWagerTypeArray
} from "./types";
import Menu from "./Menu";
import List from "./List";

export const getLegRacesList = (item: BetTypeListItem) =>
  Array.from(Array(item.legCount).keys()).map((_, index) => ({
    id: index,
    name: `Leg ${index + 1}`,
    code: item.code,
    legCount: item.legCount
  }));

const BetTypeSelectorMobile = ({
  betTypesList,
  selectedBetType,
  selectedBetLeg,
  currentRaceId,
  onSelectBetType,
  onSelectBetLeg
}: BetTypeSelectorProps) => {
  const betSelections = useSelector(getBetSelections);
  const prevBetTypeId = useRef<number>();

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [selectedBetTypeName, setSelectedBetTypeName] = useState("");
  const [subWagerList, setSubWagerList] = useState<BetTypeListItem[]>([]);
  const [activeBetType, setActiveBetType] = useState<BetTypeListItem>();
  const [hasSelectedTypeRead, setSelectedTypeRead] = useState(true);

  const groupWagerList = useMemo(
    () => groupByWagerType(betTypesList),
    [betTypesList]
  );

  const onSelectBetTypeFromMainList = useCallback(
    (item: BetTypeListItem, items: BetTypeListItem[]) => {
      const list = item.legCount > 1 ? getLegRacesList(item) : items;

      if (items.length > 1 || item.legCount > 1) {
        setSelectedBetTypeName(get(items, "[0].name", ""));
        setSubWagerList(list as BetTypeListItem[]);
        setIsMenuOpen(true);
      } else {
        setIsMenuOpen(false);
      }

      if ((hasSelectedTypeRead && item.id) || item.id !== activeBetType?.id) {
        setActiveBetType(item);
        setSelectedTypeRead(true);

        if (selectedBetType !== item.id) {
          onSelectBetType(item.id);
        }
      }
    },
    [selectedBetType, activeBetType, hasSelectedTypeRead]
  );

  const onSelectBetTypeById = useCallback(
    (id: number) => {
      (groupWagerList as GroupWagerTypeArray).some((groupTypes) => {
        const betType = groupTypes.find((item) => item.id === id);
        if (betType) {
          onSelectBetTypeFromMainList(betType, groupTypes);
        }
        return !!betType;
      });
    },
    [groupWagerList]
  );

  const clearActiveBetType = useCallback(() => {
    prevBetTypeId.current = activeBetType?.id;

    setActiveBetType(undefined);
    setIsMenuOpen(false);
  }, [activeBetType]);

  useEffect(() => {
    if (selectedBetType === null) {
      clearActiveBetType();
    } else {
      setSelectedTypeRead(false);
      onSelectBetTypeById(selectedBetType);
      prevBetTypeId.current = undefined;
    }
  }, [selectedBetType, groupWagerList]);

  useEffect(() => {
    if (prevBetTypeId.current && flatten(betSelections).length > 0) {
      onSelectBetType(prevBetTypeId.current);
    }
  }, [JSON.stringify(betSelections)]);

  useEffect(() => {
    if (selectedBetLeg !== 0) {
      onSelectBetLeg(0);
    }
  }, [currentRaceId]);

  return (
    <Container data-qa-label="bet-type-selector">
      <Menu
        isOpen={isMenuOpen}
        activeLeg={selectedBetLeg}
        subWagerList={subWagerList}
        selectedBetType={activeBetType?.id}
        onSelectBetType={onSelectBetType}
        onSelectLegRace={onSelectBetLeg}
        onClickBack={clearActiveBetType}
        betTypeName={selectedBetTypeName}
      />
      <List
        isGroupWagerList
        betTypesList={groupWagerList as GroupWagerTypeArray}
        onSelectBetType={onSelectBetTypeFromMainList}
        selectedBetType={activeBetType?.id}
      />
    </Container>
  );
};

export default BetTypeSelectorMobile;
